import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, useMatch } from "react-router-dom";
import { Button, Grid, Group, Title, Modal, Stack, Text, Space, Badge, useMantineTheme, Drawer, Collapse } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";

import { useCreateTeamMutation, useDeleteTeamMutation, useGetTeamByIdQuery, useGetTeamsQuery, useRestoreTeamMutation, useUpdateTeamMutation } from "../../api/team.api";
import { ListTeamDto, MutableTeamDto, TeamDto } from "../../models/team.models";
import { TeamList } from "../../Components/Teams/TeamList";
import { TeamDetailView } from "../../Components/Teams/TeamDetailView";
import TeamEditView from "../../Components/Teams/TeamEditView";
import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";

const TeamsPage = () => {
	const navigate = useNavigate();
	const history = useLocation();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ teamId?: string }>();
	const isEditingTeam = useMatch("/teams/:teamId/edit") !== null;
	const { SearchBox, visible: searchVisible } = useSearch();

	const isNewTeam = params.teamId === "new";
	const teamIdFromParams = params.teamId || null;

	const { data: teams, isPending: isLoadingTeams } = useGetTeamsQuery();
	const [selectedTeam, setSelectedTeam] = useState<ListTeamDto | null>(null);
	const { data: team, isPending: isLoadingTeam } = useGetTeamByIdQuery(selectedTeam?.TeamId || teamIdFromParams || null);

	const [discardTeamChangesModalOpened, { open: openDiscardTeamChangesModal, close: closeDiscardTeamChangesModal }] = useDisclosure(false);
	const [deleteTeamModalOpen, { open: openDeleteTeamModal, close: closeDeleteTeamModal }] = useDisclosure(false);
	const [restoreTeamModalOpen, { open: openRestoreTeamModal, close: closeRestoreTeamModal }] = useDisclosure(false);
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);
	const [isTeamChanged, setIsTeamChanged] = useState(false);
	const [requestedTeamId, setRequestedTeamId] = useState<string | null>(null);

	const { mutate: createTeam, isPending: isCreatingTeam } = useCreateTeamMutation();
	const { mutate: updateTeam, isPending: isUpdatingTeam } = useUpdateTeamMutation();
	const { mutate: deleteTeam } = useDeleteTeamMutation();
	const { mutate: restoreTeam, isPending: isRestoringTeam } = useRestoreTeamMutation();

	const isSavingTeam = isCreatingTeam || isUpdatingTeam;

	useEffect(() => {
		if (teamIdFromParams) {
			const team = teams?.find((t: ListTeamDto) => t.TeamId === teamIdFromParams);
			if (team) {
				setSelectedTeam?.(team);
				if (isMobile) {
					openDetailViewDrawer();
				}
			}
		} else if (teams && teams.length > 0) {
			setSelectedTeam?.(teams[0]);
		}
	}, [teamIdFromParams, teams, isMobile]);

	useEffect(() => {
		if (!teamIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [history.pathname]);

	const handleNewTeam = () => {
		setIsTeamChanged(false);
		setSelectedTeam(null);
		navigate(`/teams/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleEditTeam = () => {
		navigate(`/teams/${selectedTeam?.TeamId}/edit`);
	};

	const handleTabSelected = (tab: string | null) => {
		navigate(`/${tab}`)
	};

	const handleTeamSelect = (team: ListTeamDto | null) => {
		if (!team) return;

		setRequestedTeamId(team.TeamId);

		if (isTeamChanged && !discardTeamChangesModalOpened) {
			openDiscardTeamChangesModal();
			return;
		}

		setSelectedTeam(team);
		setIsTeamChanged(false);
		navigate(`/teams/${team.TeamId}`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSaveTeam = (values: MutableTeamDto) => {
		if (isNewTeam) {
			createTeam(values, {
				onSuccess: (response) => {
					setIsTeamChanged(false);
					navigate(`/teams/${response.data.TeamId}`, { replace: true });
				}
			});
		} else {
			updateTeam(values, {
				onSuccess: () => {
					setIsTeamChanged(false);
					// history.replace(`/teams/${selectedTeam?.TeamId}`);
					navigate(-1);
				}
			});
		}
	}

	const handleDeleteTeam = () => {
		if (!team) return;

		closeDeleteTeamModal();
		deleteTeam(team, {
			onSuccess: () => {
				setSelectedTeam(null);
				navigate(`/teams`, { replace: true });
			}
		});
	}

	const handleRestoreTeam = () => {
		if (!team) return;

		closeRestoreTeamModal();
		restoreTeam(team, {
			onSuccess: () => {

			}
		});
	}

	const handleCloseTeam = () => {
		if (isTeamChanged && !discardTeamChangesModalOpened) {
			openDiscardTeamChangesModal();
		} else {
			closeDiscardTeamChangesModal();
			setIsTeamChanged(false);
			navigate(-1);

			if (isMobile && isNewTeam) {
				closeDetailViewDrawer();
			}
		}
	};

	const handleTeamChanged = (changed: boolean) => {
		setIsTeamChanged(changed);
	};

	const handleDiscardTeamChanges = () => {
		closeDiscardTeamChangesModal();
		setIsTeamChanged(false);

		if (isMobile && isNewTeam) {
			closeDetailViewDrawer();
		}

		if (requestedTeamId && requestedTeamId !== selectedTeam?.TeamId) {
			navigate(`/teams/${requestedTeamId}`);
			setRequestedTeamId(null);
		} else {
			navigate(-1);
		}
	};

	const handleCancelTeamDiscard = () => {
		closeDiscardTeamChangesModal();
	}

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	}

	return <>
		<DeleteTeamModal
			team={team}
			open={deleteTeamModalOpen}
			onDelete={handleDeleteTeam}
			onClose={closeDeleteTeamModal}
		/>

		<DiscardTeamChangesModal
			open={discardTeamChangesModalOpened}
			onDiscard={handleDiscardTeamChanges}
			onClose={handleCancelTeamDiscard}
		/>

		<RestoreTeamModal
			team={team}
			open={restoreTeamModalOpen}
			onRestore={handleRestoreTeam}
			onClose={closeRestoreTeamModal}
		/>

		<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
			{isEditingTeam || isNewTeam ? <>
				<TeamEditView team={!isNewTeam ? team : null} onSave={handleSaveTeam} onClose={handleCloseTeam} isSaving={isSavingTeam} onChanged={handleTeamChanged} />
			</> : (
				<TeamDetailView team={team} isLoading={isLoadingTeam} onEdit={handleEditTeam} onClose={handleDetailViewDrawerClose} onDelete={openDeleteTeamModal} onRestore={openRestoreTeamModal} isRestoring={isRestoringTeam} />
			)}
		</Drawer>

		<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
			{SearchBox}
		</Collapse>

		<Group justify="space-between">
			<Title order={2}>Users & Teams</Title>
			<Group>
				<RoleTemplate role={AzureADRoles.Admin}>
					<Button onClick={handleNewTeam} radius="xl" leftSection={<IconPlus />}>New Team</Button>
				</RoleTemplate>
			</Group>
		</Group>

		<Grid mt="lg" gutter="md">
			<Grid.Col span={{ base: 12, xs: ((!teams || teams.length === 0) && !isNewTeam && !isEditingTeam) ? 12 : 5 }}>
				<TeamList teams={teams} isLoading={isLoadingTeams} selectedTeam={selectedTeam} setSelectedTeam={handleTeamSelect} setSelectedTab={handleTabSelected} />
			</Grid.Col>
			<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
				{isEditingTeam || isNewTeam ? <>
					<TeamEditView team={!isNewTeam ? team : null} onSave={handleSaveTeam} onClose={handleCloseTeam} isSaving={isSavingTeam} onChanged={handleTeamChanged} />
				</> : teams && teams.length > 0 ? (
					<TeamDetailView team={team} isLoading={isLoadingTeam} onEdit={handleEditTeam} onDelete={openDeleteTeamModal} onRestore={openRestoreTeamModal} isRestoring={isRestoringTeam} />
				) : <></>}
			</Grid.Col>
		</Grid>
	</>
}

export default TeamsPage

const DiscardTeamChangesModal = ({ open, onDiscard, onClose }: { open: boolean, onClose: () => void, onDiscard: () => void }) => (
	<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
		<Stack>
			<Text>If you leave now, you’ll lose unsaved changes to this team.</Text>
			<Group justify="right">
				<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
				<Button variant="filled" onClick={onDiscard} radius="lg">Discard Changes</Button>
			</Group>
		</Stack>
	</Modal>
);

const DeleteTeamModal = ({ team, open, onDelete, onClose }: { team?: TeamDto, open: boolean, onDelete: () => void, onClose: () => void }) => {
	const teamHasMembers = (team?.Members.length ?? 0) > 0;
	return (
		<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Delete Team</Text>} radius="lg" zIndex={1000}>
			<Stack>
				{teamHasMembers && <>
					<Stack gap={4}>
						<Text fw="bold" mb="sm">All members must be removed from this team before it can be deleted.</Text>
						<Text>This team has the following members:</Text>
						<Group gap="xs">
							{team?.Members.map(member => (
								<Badge radius="sm" key={member.UserId}>{member.Name}</Badge>
							))}
						</Group>
					</Stack>
				</>}

				{!teamHasMembers && <>
					<Text>Are you sure you want to delete this team?</Text>
				</>}
				<Space h="xs" />
				<Group justify="right">
					<Button variant="outline" onClick={onClose} radius="lg">{teamHasMembers ? "Close" : "Cancel"}</Button>
					{!teamHasMembers &&
					<Button color="red" onClick={onDelete} radius="lg">Delete</Button>
					}
				</Group>
			</Stack>
		</Modal>
	);
}

const RestoreTeamModal = ({ team, open, onRestore, onClose }: { team?: TeamDto, open: boolean, onRestore: () => void, onClose: () => void }) => {
	return (
		<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Restore Team</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to restore {team?.Name}?</Text>
				<Space h="xs" />
				<Group justify="right">
					<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
					<Button variant="filled" onClick={onRestore} radius="lg">Restore</Button>
				</Group>
			</Stack>
		</Modal>
	);
}